import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { longest, type AgencyPublic } from "@nabla/api";
import { AuthCard } from "../features/auth/AuthCard.tsx"; import { PublicEntrances, invitedLanding } from "../features/auth/public-entrances.tsx";
import { authCopy } from "../features/auth/copy.ts";
import { agencyCommands, agencyRead } from "../client/agency.ts";
import type { ClientError } from "../client/index.ts";
import { designCopy } from "../design/copy.ts";
import { Button, Buttons } from "../design/primitives/Button.tsx";
import { TextField } from "../design/primitives/Field.tsx";
import { agencyCopy as t } from "./copy.ts";
import { goTo, openedToken } from "./navigation.ts";
import styles from "./agency.module.css";

function Lone({ title, busy = false, children }: { readonly title: string; readonly busy?: boolean; readonly children?: ReactNode }) {
  return (
    <main id="conteudo">
      <AuthCard title={title} busy={busy}>{children}</AuthCard>
    </main>
  );
}

export function Out({ reason }: { readonly reason: "terminou" | "saiu" }) {
  return (
    <Lone title={reason === "saiu" ? t.sessao.saiu : t.sessao.terminou}>
      <PublicEntrances />
      {reason === "terminou" ? <p className={styles.detail}>{t.sessao.terminouExplica}</p> : null}
    </Lone>
  );
}

export function SignOut() {
  const [done, setDone] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState(false);
  if (done) return <Out reason="saiu" />;
  const leave = () => {
    setSending(true);
    setProblem(undefined);
    void agencyCommands.signOut().then((reply) => {
      setSending(false);
      if (reply.kind === "feito") setDone(true);
      else setProblem(reply.kind === "falhou" ? inviteProblem(reply.error) : designCopy.erros.interno);
    });
  };
  return (
    <Lone title={t.sessao.sairPergunta}>
      <p className={styles.detail}>{t.sessao.sairExplica}</p>
      {problem === undefined ? null : <p className={styles.problem} role="alert">{problem}</p>}
      <Buttons>
        <Button variant="primario" onClick={leave} disabled={sending}>{t.lugares.sair}</Button>
        <Button href="/">{t.sessao.voltar}</Button>
      </Buttons>
    </Lone>
  );
}

export function NotFound() {
  return (
    <Lone title={t.naoEncontrado}>
      <Button href="/">{t.sessao.voltar}</Button>
    </Lone>
  );
}

function inviteProblem(error: ClientError): string {
  if (error.kind === "rede") return designCopy.semConexao;
  if (error.kind === "formato") return designCopy.erros.interno;
  if (error.code === "token-usado") return authCopy.convite.usado;
  if (error.code === "token-vencido") return t.convite.vencido;
  if (error.code === "token-invalido") return authCopy.convite.invalido;
  return error.code === "devagar" ? designCopy.erros.devagar : error.message;
}

export function Invite() {
  const [agency, setAgency] = useState<AgencyPublic | undefined>(undefined);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  useEffect(() => void agencyRead.publico().then((read) => read.ok && setAgency(read.value)), []);
  const title = t.convite.titulo(agency?.agency.name ?? "");
  if (openedToken === undefined) return <Lone title={authCopy.convite.invalido} />;
  const token = openedToken;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const typed = name.trim();
    if (typed === "" || sending) return;
    setSending(true);
    setProblem(undefined);
    void agencyCommands.accept(token, typed).then((reply) => {
      setSending(false);
      if (reply.kind === "feito") goTo(invitedLanding());
      else if (reply.kind === "falhou") setProblem(inviteProblem(reply.error));
    });
  };
  return (
    <Lone title={title} busy={agency === undefined}>
      <form className={styles.form} onSubmit={submit}>
        <TextField label={authCopy.convite.nome} value={name} onChange={setName} maxLength={longest.name} autoComplete="name" required />
        {problem === undefined ? null : <p className={styles.problem} role="alert">{problem}</p>}
        <Button variant="primario" type="submit" disabled={sending}>{authCopy.convite.entrar}</Button>
      </form>
    </Lone>
  );
}
